export interface VapcInfo {
  w: number
  h: number
  videoW: number
  videoH: number
  fps: number
  rgbFrame: [number, number, number, number]
  aFrame: [number, number, number, number]
}

/** 从 mp4 尾部 vapc box 解析布局，与 encodeVapMp4 / Android AnimView 一致 */
export function parseVapcFromBuffer(data: Uint8Array): VapcInfo | null {
  const marker = [0x76, 0x61, 0x70, 0x63] // 'vapc'
  let idx = -1
  for (let i = data.length - 4; i >= 4; i--) {
    if (
      data[i] === marker[0] &&
      data[i + 1] === marker[1] &&
      data[i + 2] === marker[2] &&
      data[i + 3] === marker[3]
    ) {
      idx = i
      break
    }
  }
  if (idx < 4) return null

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  const boxSize = view.getUint32(idx - 4)
  const jsonStart = idx + 4
  const jsonEnd = idx - 4 + boxSize
  if (jsonEnd <= jsonStart || jsonEnd > data.length) return null

  try {
    const json = new TextDecoder().decode(data.subarray(jsonStart, jsonEnd))
    const info = JSON.parse(json).info
    if (
      !info ||
      !Array.isArray(info.rgbFrame) ||
      info.rgbFrame.length !== 4 ||
      !Array.isArray(info.aFrame) ||
      info.aFrame.length !== 4
    ) {
      return null
    }
    return {
      w: info.w,
      h: info.h,
      videoW: info.videoW,
      videoH: info.videoH,
      fps: info.fps,
      rgbFrame: info.rgbFrame,
      aFrame: info.aFrame,
    }
  } catch {
    return null
  }
}

/** 无 vapc 时的兜底：PACKED_ALPHA + align16（512 为 1024×512；300 为 608×304） */
export function inferVapcFromVideoSize(
  videoWidth: number,
  videoHeight: number,
): VapcInfo | null {
  if (videoWidth < 2 || videoHeight < 2) return null

  // 标准 2:1，无 H.264 对齐 padding（512 → 1024×512）
  if (videoWidth === videoHeight * 2) {
    const w = videoHeight
    return {
      w,
      h: w,
      videoW: videoWidth,
      videoH: videoHeight,
      fps: 30,
      rgbFrame: [0, 0, w, w],
      aFrame: [w, 0, w, w],
    }
  }

  // align16  padding：encW=align16(2*srcW), encH=align16(srcH)（300 → 608×304）
  // 取满足条件的最小 src，避免把 304 误判为输出尺寸
  let best: VapcInfo | null = null
  for (let srcH = 1; srcH <= videoHeight; srcH++) {
    if (Math.ceil(srcH / 16) * 16 !== videoHeight) continue
    const srcW = srcH
    if (Math.ceil((srcW * 2) / 16) * 16 !== videoWidth) continue
    if (!best || srcW < best.w) {
      best = {
        w: srcW,
        h: srcH,
        videoW: videoWidth,
        videoH: videoHeight,
        fps: 30,
        rgbFrame: [0, 0, srcW, srcH],
        aFrame: [srcW, 0, srcW, srcH],
      }
    }
  }
  return best
}

/** 拉取 mp4 尾部 vapc box（CORS 允许时优先于 infer） */
export async function loadVapcFromUrl(url: string): Promise<VapcInfo | null> {
  try {
    const res = await fetch(url, { mode: 'cors' })
    if (!res.ok) return null
    const buf = new Uint8Array(await res.arrayBuffer())
    return parseVapcFromBuffer(buf)
  } catch {
    return null
  }
}
